import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Fuel, Gauge, Calendar, Cog, Palette, Car, Hash, Phone, Mail, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Layers, Settings2, CircleDot, Tag, Leaf, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { vehicleGallery, vehicleVideos } from "@/utils/vehicleImages";
import { formatVehiclePrice, isPriceNotDecided } from "@/utils/vehiclePrice";
import BotProtection from "@/components/BotProtection";
import ConsentAgreement from "@/components/ConsentAgreement";
import PhoneConsentSlide from "@/components/PhoneConsentSlide";
import { formatApiError } from "@/utils/apiError";
import { isFilled, isEmailFilled } from "@/utils/formReady";
import { vehicleDetailPath, vehicleHeadline } from "@/utils/vehicleDisplay";
import { trackLead, trackVehicleLook, trackVehicleTime } from "@/utils/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Thumbnail strip below the image dots. 0 = invisible, 1 = fully visible.
const GALLERY_PREVIEW_IDLE_OPACITY = 0.18;
const GALLERY_PREVIEW_HOVER_OPACITY = 1;

export default function VehicleModal({ vehicle, open, onClose }) {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isDark } = useTheme();
  const gallery = vehicleGallery(vehicle);
  const videos = vehicleVideos(vehicle);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  /** 'image' | 'video' — which media type is shown in the hero */
  const [mediaMode, setMediaMode] = useState("image");
  const [previewStripHovered, setPreviewStripHovered] = useState(false);
  const previewRailRef = useRef(null);

  const galleryLength = gallery.length;
  const videosLength = videos.length;

  useEffect(() => {
    setActiveImageIndex(0);
    setActiveVideoIndex(0);
    setMediaMode(galleryLength ? "image" : videosLength ? "video" : "image");
  }, [vehicle?.id, open, galleryLength, videosLength]);
  
  const [inquiryForm, setInquiryForm] = useState({ name: "", email: "", phone: "", message: "", preferred_contact: "email" });
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [consent, setConsent] = useState(false);
  const phoneProvided = isFilled(inquiryForm.phone);
  const canSubmit =
    isFilled(inquiryForm.name) &&
    isEmailFilled(inquiryForm.email) &&
    (!phoneProvided || consent);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  useEffect(() => {
    setShowMoreDetails(false);
    setShowDescription(false);
    setPreviewStripHovered(false);
    setShowInquiryForm(Boolean(vehicle?.price_not_decided));
    setConsent(false);
  }, [vehicle?.id, open, vehicle?.price_not_decided]);

  useEffect(() => {
    if (!open || !vehicle?.id) return undefined;
    const car = { id: vehicle.id, name: vehicleHeadline(vehicle) };
    trackVehicleLook(car);
    let elapsed = 0;
    let mark = performance.now();
    const onHide = () => {
      if (document.visibilityState === "hidden") {
        elapsed += performance.now() - mark;
      } else {
        mark = performance.now();
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      if (document.visibilityState !== "hidden") elapsed += performance.now() - mark;
      trackVehicleTime(car, elapsed);
    };
  }, [open, vehicle?.id, vehicle?.year, vehicle?.make, vehicle?.model, vehicle?.trim]);

  useEffect(() => {
    const rail = previewRailRef.current;
    if (!rail) return;
    const thumb = rail.querySelector(`[data-preview-index="${activeImageIndex}"]`);
    if (!thumb) return;
    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left < railRect.left || thumbRect.right > railRect.right) {
      rail.scrollTo({
        left: rail.scrollLeft + (thumbRect.left - railRect.left) - (railRect.width - thumbRect.width) / 2,
        behavior: "smooth",
      });
    }
  }, [activeImageIndex]);

  const scrollPreviewRail = (event, direction) => {
    event.preventDefault();
    event.stopPropagation();
    const el = previewRailRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.6), behavior: "smooth" });
  };

  if (!vehicle) return null;

  const updateInquiryPhone = (value) => {
    setInquiryForm((f) => ({
      ...f,
      phone: value,
      preferred_contact: !isFilled(value) && f.preferred_contact === "phone" ? "email" : f.preferred_contact,
    }));
    if (!isFilled(value)) setConsent(false);
  };

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    const phone = inquiryForm.phone.trim();
    if (phone && !consent) {
      toast.error(t('consent.formRequired'));
      return;
    }
    const preferredContact = phone && inquiryForm.preferred_contact === "phone"
      ? "phone"
      : inquiryForm.preferred_contact === "phone"
        ? "email"
        : inquiryForm.preferred_contact;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/inquiry`, {
        vehicle_id: vehicle.id,
        ...inquiryForm,
        phone,
        preferred_contact: preferredContact,
        consent: Boolean(phone && consent),
        captcha_token: captchaToken,
      });
      toast.success(response.data.message);
      trackLead("vehicle_inquiry", { item_id: vehicle.id });
      setInquiryForm({ name: "", email: "", phone: "", message: "", preferred_contact: "email" });
      setConsent(false);
      setShowInquiryForm(false);
    } catch (error) {
      toast.error(formatApiError(error, "Failed to submit inquiry. Please try again."));
    } finally { setSubmitting(false); }
  };

  // Fixed option labels (condition / fuel / transmission) stay in static i18n
  const translateVehicleValue = (raw, map) => {
    if (raw == null || raw === "") return raw;
    const key = String(raw).trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");
    const tKey = map[key];
    if (!tKey) return raw;
    const translated = t(tKey);
    return translated.startsWith("vehicleModal.") ? raw : translated;
  };

  /** Prefer Google-translated Spanish field; fall back to English source. */
  const localizedText = (en, es) => {
    if (language === "es") {
      const s = typeof es === "string" ? es.trim() : "";
      if (s) return s;
    }
    return en ?? "";
  };

  const localizedList = (enList, esList) => {
    const en = Array.isArray(enList) ? enList.filter((x) => typeof x === "string" && x.trim()) : [];
    const es = Array.isArray(esList) ? esList.filter((x) => typeof x === "string" && x.trim()) : [];
    if (language === "es" && es.length > 0) return es;
    return en;
  };

  const conditionValueMap = {
    new: "vehicleModal.values.conditionNew",
    used: "vehicleModal.values.conditionUsed",
    "certified pre owned": "vehicleModal.values.conditionCertified",
    "certified pre-owned": "vehicleModal.values.conditionCertified",
    "certified preowned": "vehicleModal.values.conditionCertified",
    cpo: "vehicleModal.values.conditionCertified",
  };

  const fuelValueMap = {
    gasoline: "vehicleModal.values.fuelGasoline",
    gas: "vehicleModal.values.fuelGasoline",
    petrol: "vehicleModal.values.fuelGasoline",
    electric: "vehicleModal.values.fuelElectric",
    diesel: "vehicleModal.values.fuelDiesel",
    hybrid: "vehicleModal.values.fuelHybrid",
  };

  const transmissionValueMap = {
    automatic: "vehicleModal.values.transmissionAutomatic",
    auto: "vehicleModal.values.transmissionAutomatic",
  };

  const displayEngine = localizedText(vehicle.engine, vehicle.engine_es);
  const displayExterior = localizedText(vehicle.exterior_color, vehicle.exterior_color_es);
  const displayInterior = localizedText(vehicle.interior_color, vehicle.interior_color_es);
  const displayBodyType = localizedText(vehicle.body_type, vehicle.body_type_es);
  const displayFeatures = localizedList(vehicle.features, vehicle.features_es);

  const specs = [
    { icon: Calendar, label: t('vehicleModal.year'), value: vehicle.year },
    { icon: Gauge, label: t('vehicleModal.mileage'), value: `${vehicle.mileage.toLocaleString()} mi` },
    { icon: Fuel, label: t('vehicleModal.fuelType'), value: translateVehicleValue(vehicle.fuel_type, fuelValueMap) },
    { icon: Cog, label: t('vehicleModal.transmission'), value: translateVehicleValue(vehicle.transmission, transmissionValueMap) },
    { icon: Car, label: t('vehicleModal.engine'), value: displayEngine },
    { icon: Palette, label: t('vehicleModal.exteriorColor'), value: displayExterior },
    { icon: Palette, label: t('vehicleModal.interiorColor'), value: displayInterior },
    { icon: Hash, label: "VIN", value: vehicle.vin },
  ];

  // Customer-facing extra specs — stockNumber intentionally excluded
  const moreSpecs = [
    { icon: Tag, label: t("vehicleModal.trim"), value: vehicle.trim },
    { icon: Layers, label: t("vehicleModal.bodyType"), value: displayBodyType || null },
    { icon: Leaf, label: t("vehicleModal.condition"), value: translateVehicleValue(vehicle.condition, conditionValueMap) },
    { icon: Settings2, label: t("vehicleModal.drivetrain"), value: vehicle.drivetrain },
    { icon: CircleDot, label: t("vehicleModal.cylinders"), value: vehicle.cylinders },
    { icon: Fuel, label: t("vehicleModal.cityMpg"), value: vehicle.city_mpg != null && vehicle.city_mpg !== "" ? vehicle.city_mpg : null },
    { icon: Fuel, label: t("vehicleModal.highwayMpg"), value: vehicle.hwy_mpg != null && vehicle.hwy_mpg !== "" ? vehicle.hwy_mpg : null },
  ].filter((s) => s.value !== null && s.value !== undefined && String(s.value).trim() !== "");

  const descriptionPoints = localizedList(
    vehicle.description_points,
    vehicle.description_points_es
  ).slice(0, 4);

  const hasMoreDetails = moreSpecs.length > 0;
  const hasDescription = descriptionPoints.length > 0;
  const showDetailsPanel = showMoreDetails && hasMoreDetails;
  const showDescriptionPanel = showDescription && hasDescription;

  const inputCls = isDark
    ? "bg-black/50 border-white/10 text-white h-12 rounded-none"
    : "bg-white border-gray-300 text-gray-900 h-12 rounded-none";

  const toggleBtnCls = `w-full sm:w-auto rounded-none font-semibold uppercase tracking-wider text-sm ${
    isDark
      ? "border-white/15 text-white hover:bg-white/10 hover:text-white"
      : "border-gray-300 text-gray-900 hover:bg-gray-50 hover:text-gray-900"
  }`;

  const renderSpecGrid = (items) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((spec) => (
        <div key={spec.label} className={`p-4 border ${isDark ? 'bg-[#121212] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
          <div className={`flex items-center gap-2 mb-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            <spec.icon className="h-4 w-4" />
            <span className="text-xs uppercase tracking-wider">{spec.label}</span>
          </div>
          <p className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{spec.value}</p>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`max-w-4xl w-full p-0 !block !gap-0 overflow-x-hidden overflow-y-auto max-h-[90vh] [&>button]:backdrop-blur-sm [&>button]:hover:opacity-100 [&>button]:rounded-none [&>button]:p-2 [&>button]:top-4 [&>button]:right-4 [&>button]:z-10 ${
        isDark
          ? 'bg-[#0A0A0A] border-white/10 [&>button]:bg-black/50 [&>button]:text-white [&>button]:hover:bg-black/70'
          : 'bg-white border-gray-200 [&>button]:bg-white/80 [&>button]:text-gray-900 [&>button]:hover:bg-gray-100'
      }`} data-testid="vehicle-modal">
        <DialogTitle className="sr-only">{vehicle.year} {vehicle.make} {vehicle.model}</DialogTitle>
        
        <div className="relative w-full aspect-video bg-black">
          {mediaMode === "video" && videos.length > 0 ? (
            <video
              key={videos[Math.min(activeVideoIndex, videos.length - 1)]?.url}
              src={videos[Math.min(activeVideoIndex, videos.length - 1)]?.url}
              className="w-full h-full object-contain bg-black"
              controls
              playsInline
              preload="metadata"
            />
          ) : gallery.length > 0 ? (
            <img
              src={gallery[Math.min(activeImageIndex, gallery.length - 1)]}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              No media
            </div>
          )}
          {mediaMode === "image" && isDark && (
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
          )}
          <div className="absolute top-4 left-4 z-10 flex flex-wrap gap-2">
            <span className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider ${vehicle.status === "available" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
              {vehicle.status === "available" ? t('featured.available') : t('featured.sold')}
            </span>
            {(gallery.length > 0 || videos.length > 0) && (
              <div className="flex overflow-hidden border border-white/20">
                {gallery.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setMediaMode("image")}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                      mediaMode === "image" ? "bg-white text-black" : "bg-black/60 text-white hover:bg-black/80"
                    }`}
                  >
                    Photos
                  </button>
                )}
                {videos.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setMediaMode("video")}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                      mediaMode === "video" ? "bg-white text-black" : "bg-black/60 text-white hover:bg-black/80"
                    }`}
                  >
                    Videos ({videos.length})
                  </button>
                )}
              </div>
            )}
          </div>
          {mediaMode === "image" && gallery.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={() => setActiveImageIndex((i) => (i - 1 + gallery.length) % gallery.length)}
                className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-none ${isDark ? 'bg-black/60 text-white hover:bg-black/80' : 'bg-white/80 text-gray-900 hover:bg-white'}`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={() => setActiveImageIndex((i) => (i + 1) % gallery.length)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-none ${isDark ? 'bg-black/60 text-white hover:bg-black/80' : 'bg-white/80 text-gray-900 hover:bg-white'}`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center gap-1.5 px-4 pointer-events-auto">
                {gallery.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    aria-label={`Show image ${idx + 1}`}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === activeImageIndex
                        ? isDark ? 'w-6 bg-blue-500' : 'w-6 bg-red-600'
                        : 'w-1.5 bg-white/50 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          {mediaMode === "video" && videos.length > 1 && (
            <div className="absolute bottom-3 left-3 right-3 z-20 flex flex-wrap gap-1.5 pointer-events-auto">
              {videos.map((v, idx) => (
                <button
                  key={`${v.url}-${idx}`}
                  type="button"
                  onClick={() => setActiveVideoIndex(idx)}
                  className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${
                    idx === activeVideoIndex
                      ? isDark ? "bg-blue-600 border-blue-500 text-white" : "bg-red-600 border-red-600 text-white"
                      : "bg-black/60 border-white/30 text-white/80 hover:bg-black/80"
                  }`}
                >
                  Video {idx + 1}
                </button>
              ))}
            </div>
          )}
        </div>

        {mediaMode === "image" && gallery.length > 1 && (
          <div
            className="w-full max-w-full min-w-0 px-3 py-3"
            onMouseEnter={() => setPreviewStripHovered(true)}
            onMouseLeave={() => setPreviewStripHovered(false)}
          >
            <div className="flex items-center gap-1.5 w-full min-w-0 h-12">
              <button
                type="button"
                aria-label="Scroll preview strip left"
                onClick={(event) => scrollPreviewRail(event, -1)}
                className={`shrink-0 p-1.5 opacity-100 ${
                  isDark
                    ? "bg-white/20 text-white hover:bg-white/30"
                    : "bg-gray-200 text-gray-900 hover:bg-gray-300"
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div
                ref={previewRailRef}
                className="min-w-0 flex-1 h-12 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden transition-opacity duration-200"
                style={{
                  opacity: previewStripHovered
                    ? GALLERY_PREVIEW_HOVER_OPACITY
                    : GALLERY_PREVIEW_IDLE_OPACITY,
                  scrollbarWidth: "none",
                }}
              >
                <div className="flex items-center gap-1.5 h-12">
                  {gallery.map((src, idx) => (
                    <button
                      key={`${src}-${idx}`}
                      type="button"
                      data-preview-index={idx}
                      aria-label={`Show image ${idx + 1}`}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`relative w-16 h-11 overflow-hidden border-2 shadow-md shrink-0 ${
                        idx === activeImageIndex
                          ? isDark
                            ? "border-blue-500 ring-1 ring-blue-500/50"
                            : "border-red-600 ring-1 ring-red-600/40"
                          : isDark
                            ? "border-white/40"
                            : "border-gray-300"
                      }`}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                aria-label="Scroll preview strip right"
                onClick={(event) => scrollPreviewRail(event, 1)}
                className={`shrink-0 p-1.5 opacity-100 ${
                  isDark
                    ? "bg-white/20 text-white hover:bg-white/30"
                    : "bg-gray-200 text-gray-900 hover:bg-gray-300"
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
            <div>
              <span className={`text-sm uppercase tracking-widest font-medium ${isDark ? 'text-blue-500' : 'text-red-600'}`}>{vehicle.make}</span>
              <h2 className={`font-unbounded text-2xl md:text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{vehicle.year} {vehicle.model}{vehicle.trim ? ` ${vehicle.trim}` : ''}</h2>
            </div>
            <div className="text-right max-w-xs">
              {formatVehiclePrice(vehicle) ? (
                <>
                  <span className={`font-unbounded text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatVehiclePrice(vehicle)}</span>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Plus taxes & fees</p>
                </>
              ) : (
                <>
                  <span className={`font-unbounded text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('vehicleModal.getQuote')}</span>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('vehicleModal.quotePrompt')}</p>
                </>
              )}
            </div>
          </div>

          <div className="mb-4">
            {renderSpecGrid(specs)}
          </div>

          <div className="mb-8">
              <div className="flex flex-col sm:flex-row gap-3">
                {hasMoreDetails && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowMoreDetails((v) => !v)}
                    className={toggleBtnCls}
                    data-testid="view-more-details-btn"
                  >
                    {showMoreDetails ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-2" />
                        {t("vehicleModal.hideDetails")}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-2" />
                        {t("vehicleModal.viewMoreDetails")}
                      </>
                    )}
                  </Button>
                )}
                {hasDescription && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDescription((v) => !v)}
                    className={toggleBtnCls}
                    data-testid="view-vehicle-description-btn"
                  >
                    {showDescription ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-2" />
                        {t("vehicleModal.hideVehicleDescription")}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-2" />
                        {t("vehicleModal.viewVehicleDescription")}
                      </>
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const path = vehicleDetailPath(vehicle);
                    onClose();
                    navigate(path);
                  }}
                  className={toggleBtnCls}
                  data-testid="view-vehicle-page-btn"
                >
                  <ArrowUpRight className="h-4 w-4 mr-2" />
                  {t("vehicleModal.viewVehiclePage")}
                </Button>
              </div>

              {/* Independent expand animations so the second panel also slides open */}
              <div className="space-y-0">
                <AnimatePresence initial={false}>
                  {showDetailsPanel && (
                    <motion.div
                      key="more-details-panel"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4">
                        {renderSpecGrid(moreSpecs)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence initial={false}>
                  {showDescriptionPanel && (
                    <motion.div
                      key="description-panel"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div
                        className={`mt-4 p-5 border ${
                          isDark
                            ? "bg-[#121212] border-white/10"
                            : "bg-gray-50 border-gray-200"
                        }`}
                        data-testid="vehicle-description-panel"
                      >
                        <h4
                          className={`font-unbounded text-sm font-semibold uppercase tracking-wider mb-4 ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {t("vehicleModal.descriptionHeading")}
                        </h4>
                        <ul className="space-y-3">
                          {descriptionPoints.map((point, index) => (
                            <li
                              key={`${index}-${point.slice(0, 24)}`}
                              className={`flex items-start gap-3 ${
                                isDark ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              <span
                                className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                                  isDark ? "bg-blue-500" : "bg-red-600"
                                }`}
                                aria-hidden
                              />
                              <span className="text-sm leading-relaxed font-manrope">
                                {point}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

          <div className="mb-8">
            <h3 className={`font-unbounded text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('vehicleModal.features')}</h3>
            <div className="grid grid-cols-2 gap-3">
              {displayFeatures.map((feature, index) => (
                <div key={index} className={`flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Check className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-blue-500' : 'text-red-600'}`} />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!showInquiryForm ? (
              <motion.div key="buttons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col sm:flex-row gap-4">
                <Button onClick={() => setShowInquiryForm(true)}
                  className={`flex-1 text-white rounded-none py-6 font-semibold uppercase tracking-wider ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="inquire-btn">
                  <Mail className="mr-2 h-4 w-4" />{isPriceNotDecided(vehicle) ? t('vehicleModal.getQuote') : t('vehicleModal.interestedTitle')}
                </Button>
                <a href="tel:+17082226100" className="flex-1">
                  <Button variant="outline"
                    className={`w-full rounded-none py-6 font-semibold uppercase tracking-wider ${isDark ? 'border-white/20 text-white hover:bg-white/10 hover:text-white' : 'border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900'}`}
                    data-testid="call-btn">
                    <Phone className="mr-2 h-4 w-4" />{t('vehicleModal.orCall')} (708) 222-6100
                  </Button>
                </a>
              </motion.div>
            ) : (
              <motion.form key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                onSubmit={handleSubmitInquiry} className="space-y-4" data-testid="inquiry-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-unbounded text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('vehicleModal.sendInquiry')}</h3>
                  <button type="button" onClick={() => setShowInquiryForm(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}><X className="h-5 w-5" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inquiry-name" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.nameLabel')} *</Label>
                    <Input id="inquiry-name" value={inquiryForm.name} onChange={(e) => setInquiryForm(f => ({ ...f, name: e.target.value }))} required placeholder={t('vehicleModal.namePlaceholder')} className={inputCls} data-testid="inquiry-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inquiry-email" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.emailLabel')} *</Label>
                    <Input id="inquiry-email" type="email" value={inquiryForm.email} onChange={(e) => setInquiryForm(f => ({ ...f, email: e.target.value }))} required placeholder={t('vehicleModal.emailPlaceholder')} className={inputCls} data-testid="inquiry-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-phone" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.phoneLabel')} ({t('common.optional')})</Label>
                  <Input id="inquiry-phone" type="tel" value={inquiryForm.phone} onChange={(e) => updateInquiryPhone(e.target.value)} placeholder={t('vehicleModal.phonePlaceholder')} className={inputCls} data-testid="inquiry-phone-input" />
                </div>
                <PhoneConsentSlide open={phoneProvided}>
                  <ConsentAgreement
                    id="inquiry-consent"
                    checked={consent}
                    onCheckedChange={setConsent}
                    isDark={isDark}
                    testId="inquiry-consent-checkbox"
                    checkboxClassName={`mt-1 ${isDark ? '' : 'border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600'}`}
                  />
                </PhoneConsentSlide>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-message" className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('contact.messageLabel')} ({t('common.optional')})</Label>
                  <Textarea id="inquiry-message" value={inquiryForm.message} onChange={(e) => setInquiryForm(f => ({ ...f, message: e.target.value }))} placeholder={t('vehicleModal.messagePlaceholder')}
                    className={`rounded-none min-h-[100px] resize-none ${isDark ? 'bg-black/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-testid="inquiry-message-input" />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('vehicleModal.preferredContact')}</Label>
                  <RadioGroup value={inquiryForm.preferred_contact} onValueChange={(value) => setInquiryForm(f => ({ ...f, preferred_contact: value }))} className="flex gap-6">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="email" id="contact-email" className={isDark ? 'border-white/20 text-blue-500' : 'border-gray-400 text-red-600'} />
                      <Label htmlFor="contact-email" className={`cursor-pointer ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('contact.emailLabel')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="phone" id="contact-phone" disabled={!phoneProvided} className={isDark ? 'border-white/20 text-blue-500' : 'border-gray-400 text-red-600'} />
                      <Label htmlFor="contact-phone" className={`cursor-pointer ${phoneProvided ? '' : 'opacity-50'} ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('contact.phoneLabel')}</Label>
                    </div>
                  </RadioGroup>
                </div>
                <BotProtection onToken={setCaptchaToken} isDark={isDark} />
                <Button type="submit" disabled={submitting || !canSubmit}
                  className={`w-full text-white rounded-none py-6 font-semibold uppercase tracking-wider disabled:opacity-50 ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="submit-inquiry-btn">
                  {submitting ? t('vehicleModal.sending') : t('vehicleModal.sendInquiry')}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
